import React, { memo, useCallback, useEffect, useState } from "react";
import { Handle, Position, useReactFlow } from "@xyflow/react";
import { SelectionOverlay } from "./SelectionOverlay";
import { getAdaptiveFontSize } from "../utils";

const getPentagonPoints = (width, height) => {
  return [
    { x: width / 2, y: 0 },
    { x: width, y: height * 0.38 },
    { x: width * 0.81, y: height },
    { x: width * 0.19, y: height },
    { x: 0, y: height * 0.38 },
  ];
};

const getHexagonPoints = (width, height) => {
  return [
    { x: width * 0.25, y: 0 },
    { x: width * 0.75, y: 0 },
    { x: width, y: height / 2 },
    { x: width * 0.75, y: height },
    { x: width * 0.25, y: height },
    { x: 0, y: height / 2 },
  ];
};

const BorderNodeContent = ({ data, selected, id, width, height, ...props }) => {
  const { getNode } = useReactFlow();
  const [nodePosition, setNodePosition] = useState({ x: 0, y: 0 });
  // Get position from React Flow node and update when node moves
  useEffect(() => {
    const updatePosition = () => {
      const node = getNode(id);
      if (node) {
        setNodePosition(node.position);
      }
    };
    updatePosition();
    // Update position periodically to catch node movements
    const interval = setInterval(updatePosition, 100);
    return () => clearInterval(interval);
  }, [id, getNode]);
  const {
    label,
    borderWidth = 1,
    borderColor = "#374151",
    borderStyle = "solid",
    backgroundColor = "rgba(156, 163, 175, 0.4)",
    cornerRadius = 8,
    rotation = 0,
    shadowSettings,
    shapeType,
  } = data;

  // Use React Flow's width/height if available, otherwise use data
  const nodeWidth = width || data.width || 250;
  const nodeHeight = height || data.height || 150;

  // Calculate proportional font size for label based on node dimensions and label length.
  const labelFontSize = getAdaptiveFontSize({
    width: nodeWidth,
    height: nodeHeight,
    text: label || "",
    base: 10,
    min: 4,
    max: 14,
    padding: 12,
  });

  const shadowStyle = shadowSettings
    ? {
        boxShadow: `${shadowSettings.offsetX}px ${shadowSettings.offsetY}px ${shadowSettings.blur}px ${shadowSettings.color}`,
      }
    : {};

  const handleResize = useCallback(
    (newWidth, newHeight, newPosition) => {
      const resizeEvent = new CustomEvent("nodeResize", {
        detail: {
          nodeId: id,
          nodeType: "border",
          width: Math.round(newWidth),
          height: Math.round(newHeight),
          position: newPosition,
        },
      });
      window.dispatchEvent(resizeEvent);
    },
    [id],
  );

  const handleRotate = useCallback(
    (newRotation) => {
      const rotateEvent = new CustomEvent("nodeRotate", {
        detail: {
          nodeId: id,
          nodeType: "border",
          rotation: newRotation,
        },
      });
      window.dispatchEvent(rotateEvent);
    },
    [id],
  );

  // Check if node is locked
  const isLocked = data.locked || false;
  const flippedH = data.flippedH || false;
  const flippedV = data.flippedV || false;

  const getDashArray = (style) => {
    switch (style) {
      case "dashed": return "10,5";
      case "dotted": return "2,3";
      default: return "none";
    }
  };

  return (
    <>
      {/* NodeResizer/SelectionOverlay must be direct child of node for React Flow to work properly */}
      {!isLocked && selected && (
        <SelectionOverlay
          selected={selected || false}
          onRotate={handleRotate}
          onResize={handleResize}
          minWidth={50}
          minHeight={30}
          nodeId={id}
          width={nodeWidth}
          height={nodeHeight}
          rotation={rotation}
          nodeX={nodePosition.x}
          nodeY={nodePosition.y}
        />
      )}
      <div
        className="border-node w-full h-full relative"
        style={{
          border: !shapeType || shapeType === "rectangle" || shapeType === "circle" 
            ? `${borderWidth}px ${borderStyle} ${borderColor}` 
            : "none",
          borderRadius: shapeType === "circle" ? "999px" : `${cornerRadius}px`,
          transform: `rotate(${rotation}deg) scale(${flippedH ? -1 : 1}, ${flippedV ? -1 : 1})`,
          backgroundColor: shapeType === "pentagon" || shapeType === "hexagon" || shapeType === "triangle" 
            ? "transparent" 
            : backgroundColor,
          position: "relative",
          ...shadowStyle,
        }}
      >
        {/* Render shape based on shapeType for non-rectangle/circle shapes */}
        {shapeType === "pentagon" && (
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 180 180"
            preserveAspectRatio="none"
          >
            <polygon
              points="90,0 180,68 146,180 34,180 0,68"
              style={{
                fill: backgroundColor,
                stroke: borderColor,
                strokeWidth: borderWidth,
                strokeDasharray: getDashArray(borderStyle),
              }}
            />
          </svg>
        )}
        {shapeType === "hexagon" && (
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 180 180"
            preserveAspectRatio="none"
          >
            <polygon
              points="45,0 135,0 180,90 135,180 45,180 0,90"
              style={{
                fill: backgroundColor,
                stroke: borderColor,
                strokeWidth: borderWidth,
                strokeDasharray: getDashArray(borderStyle),
              }}
            />
          </svg>
        )}
        {shapeType === "triangle" && (
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 180 180"
            preserveAspectRatio="none"
          >
            <polygon
              points="90,0 180,180 0,180"
              style={{
                fill: backgroundColor,
                stroke: borderColor,
                strokeWidth: borderWidth,
                strokeDasharray: getDashArray(borderStyle),
              }}
            />
          </svg>
        )}

        {/* Label */}
        {label && (
          <div
            className="absolute top-1 left-1 font-medium text-gray-700 bg-white/80 px-1 rounded z-20 overflow-hidden text-ellipsis whitespace-nowrap max-w-[90%]"
            style={{ fontSize: `${labelFontSize}px` }}
            title={label}
          >
            {label}
          </div>
        )}

        {/* Handles for connections */}
        <Handle
          type="target"
          position={Position.Top}
          className="w-2 h-2 bg-blue-500 border-2 border-white opacity-0 hover:opacity-100"
        />

        <Handle
          type="source"
          position={Position.Bottom}
          className="w-2 h-2 bg-blue-500 border-2 border-white opacity-0 hover:opacity-100"
        />

        <Handle
          type="target"
          position={Position.Left}
          className="w-2 h-2 bg-blue-500 border-2 border-white opacity-0 hover:opacity-100"
        />

        <Handle
          type="source"
          position={Position.Right}
          className="w-2 h-2 bg-blue-500 border-2 border-white opacity-0 hover:opacity-100"
        />
      </div>
    </>
  );
};

export const BorderNode = memo(BorderNodeContent);

BorderNode.displayName = "BorderNode";
